import { type ActionEvent, Controller } from "@hotwired/stimulus"

import TabsController from "../tabs_controller"

import { formatDMMM, formatMMMM } from "@/helpers/date_format"

export default class extends Controller {
  static outlets = ["tabs"]
  declare readonly tabsOutlet: TabsController

  static targets = [
    "confirmButton",
    "dateInput",
    "dateRadio",
    "dateSummary",
    "flexibleDayRadio",
    "flexibleFieldset",
    "flexibleMonthCheckbox",
    "hiddenArrivalDateInput",
    "resetButton",
    "specificFieldset",
    "skipButton"
  ]
  declare readonly confirmButtonTarget: HTMLButtonElement
  declare readonly hasConfirmButtonTarget: boolean
  declare readonly dateInputTarget: HTMLInputElement
  declare readonly hasDateInputTarget: boolean
  declare readonly dateRadioTargets: HTMLInputElement[]
  declare readonly dateSummaryTarget: HTMLElement
  declare readonly flexibleDayRadioTargets: HTMLInputElement[]
  declare readonly flexibleFieldsetTarget: HTMLFieldSetElement
  declare readonly flexibleMonthCheckboxTargets: HTMLInputElement[]
  declare readonly hiddenArrivalDateInputTarget: HTMLInputElement
  declare readonly resetButtonTarget: HTMLButtonElement
  declare readonly hasResetButtonTarget: boolean
  declare readonly specificFieldsetTarget: HTMLFieldSetElement
  declare readonly skipButtonTarget: HTMLButtonElement
  declare readonly hasSkipButtonTarget: boolean

  private lazyMonthsLoaded = false

  // Manually track selected radio so we can un-check it on click.
  declare selectedDateRadio?: HTMLInputElement

  connect(): void {
    this.setSelectedDateRadio()
  }

  deselectAllDates(): void {
    this.deselectSpecificDate()
    this.deselectFlexibleDates()
  }

  deselectIfEmpty({ currentTarget }: ActionEvent): void {
    if (!(currentTarget as HTMLInputElement).value) {
      this.deselectAllDates()
    }
  }

  deselectSpecificDate(): void {
    // To support deselecting date radios, the action must fire on `click` instead of `change`, so
    // we must click rather than `radio.checked = false` here. That means we must ensure the
    // containing fieldset is enabled first, otherwise `click` won't work.
    const { disabled } = this.specificFieldsetTarget

    this.specificFieldsetTarget.disabled = false
    this.selectedDateRadio?.click()
    this.specificFieldsetTarget.disabled = disabled
  }

  get isDateSelected(): boolean {
    // Note: This does not deem "Any" to be a selection.
    return !!this.selectedDateRadio || this.hasFlexibleDate
  }

  onCalendarTabActivate({ detail: { index } }: DispatchEvent): void {
    const isSpecificTab = index === 0

    this.specificFieldsetTarget.disabled = !isSpecificTab
    this.flexibleFieldsetTarget.disabled = isSpecificTab

    if (this.lazyMonthsLoaded) {
      this.setDateSummary()
    }

    this.updateConfirmButton()
  }

  onLazyMonthsLoaded(): void {
    this.lazyMonthsLoaded = true
    this.setSelectedDateRadio()
  }

  selectCalendarTab(): void {
    this.tabsOutlet.activateTabAtIndex(this.isFlexibleTabActive ? 1 : 0)
  }

  selectDate({ currentTarget }: ActionEvent): void {
    const dateRadio = currentTarget as HTMLInputElement

    // Un-check radio if already selected
    if (dateRadio === this.selectedDateRadio) {
      dateRadio.checked = false
      this.selectedDateRadio = undefined
      this.hiddenArrivalDateInputTarget.value = ""
    } else {
      this.selectedDateRadio = dateRadio
      this.hiddenArrivalDateInputTarget.value = dateRadio.value
    }

    this.onDateSelect()
  }

  selectFlexibleDay({ currentTarget }: ActionEvent): void {
    if (currentTarget === this.defaultFlexibleDayRadio) {
      const disableDefault = !this.hasFlexibleMonth

      // If no months selected, disable default radio so it's not submitted to server.
      this.defaultFlexibleDayRadio.disabled = disableDefault

      // Disabling radio on click causes dialog to close because focus is immediately lost, so
      // re-focus input.
      if (disableDefault && this.hasDateInputTarget) {
        this.dateInputTarget.focus()
      }
    } else {
      this.defaultFlexibleDayRadio.disabled = false
    }

    this.onDateSelect()
  }

  selectFlexibleMonth(): void {
    // If no months selected and default radio is selected, disable it so it's not submitted to server.
    this.defaultFlexibleDayRadio.disabled =
      this.defaultFlexibleDayRadio.checked && !this.hasFlexibleMonth

    this.onDateSelect()
  }

  setDateSummary(): void {
    const { summaryText } = this

    if (this.hasDateInputTarget) {
      this.dateInputTarget.value = summaryText
    } else {
      this.dateSummaryTarget.textContent = summaryText
    }
  }

  updateConfirmButton(): void {
    if (this.hasConfirmButtonTarget) {
      const isDateSelectedInActiveTab =
        (this.isFlexibleTabActive && this.hasFlexibleDate) ||
        (!this.isFlexibleTabActive && !!this.selectedDateRadio)

      this.confirmButtonTarget.disabled = !isDateSelectedInActiveTab
      this.confirmButtonTarget.textContent = isDateSelectedInActiveTab ? "Add date" : "Select date"
    }
  }

  updateSkipAndResetButtons(): void {
    if (this.hasResetButtonTarget && this.hasSkipButtonTarget) {
      this.resetButtonTarget.hidden = !this.isDateSelected
      this.skipButtonTarget.hidden = this.isDateSelected
    }
  }

  private get defaultFlexibleDayRadio(): HTMLInputElement {
    return this.flexibleDayRadioTargets.at(0)!
  }

  private deselectFlexibleDates(): void {
    this.defaultFlexibleDayRadio.checked = true
    this.defaultFlexibleDayRadio.dispatchEvent(new Event("change"))
    this.flexibleMonthCheckboxTargets.forEach(this.deselectWithEvent.bind(this))
  }

  private deselectWithEvent(input: HTMLInputElement): void {
    if (input.checked) {
      input.checked = false
      input.dispatchEvent(new Event("change"))
    }
  }

  private get hasFlexibleDate(): boolean {
    return this.hasFlexibleDay || this.hasFlexibleMonth
  }

  private get hasFlexibleDay(): boolean {
    // Note the extra condition here -- the default flexible day doesn't "count" as a selected flexible day.
    return this.flexibleDayRadioTargets.some(
      (radio) => radio.checked && radio !== this.defaultFlexibleDayRadio
    )
  }

  private get hasFlexibleMonth(): boolean {
    return this.flexibleMonthCheckboxTargets.some((checkbox) => checkbox.checked)
  }

  private get isFlexibleTabActive(): boolean {
    return !this.flexibleFieldsetTarget.disabled
  }

  private onDateSelect(): void {
    this.setDateSummary()
    this.updateConfirmButton()
    this.updateSkipAndResetButtons()
  }

  private setSelectedDateRadio(): void {
    this.selectedDateRadio = this.dateRadioTargets.find((radio) => radio.checked)
  }

  private get summaryText(): string {
    if (this.isFlexibleTabActive) {
      const flexibleDayRadio = this.flexibleDayRadioTargets.find((radio) => radio.checked)
      const flexibleMonths = this.flexibleMonthCheckboxTargets
        .flatMap((checkbox) => (checkbox.checked ? [formatMMMM(new Date(checkbox.value))] : []))
        .join(", ")
      const anyFlexibleMonths = flexibleMonths.length > 0

      if (!flexibleDayRadio || flexibleDayRadio === this.defaultFlexibleDayRadio) {
        return `Any${anyFlexibleMonths ? ` day in ${flexibleMonths}` : ""}`
      } else {
        return `Any ${flexibleDayRadio.value}${anyFlexibleMonths ? ` in ${flexibleMonths}` : ""}`
      }
    } else {
      const { selectedDateRadio } = this
      return selectedDateRadio ? formatDMMM(new Date(selectedDateRadio.value)) : "Any"
    }
  }
}
